// The corporate record book, as a book.
//
// The admin screen at /dashboard/corporate-records is where the register is edited.
// This is the same register bound: a board cover, a spine with three rings, index
// tabs down the side, and one letter-size sheet per page, numbered. It exists
// because a lawyer, a lender or an auditor asks to be handed the minute book, and a
// scrolling admin table is not that — and because what prints from here is what goes
// in the physical binder.
//
// The pages are composed from the database on every view, so the book cannot drift
// from the register the way a generated file would.
//
// The visual language is deliberately the one already used for Marco Polo Schools,
// Inc. and Sandra Rossi Education Center, Inc. — three corporations whose books
// should look like a set, because they are one owner's.

import React, { useState, useEffect, useMemo } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Link } from 'react-router-dom';

const fmt = (d) => d
    ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
const num = (n) => (n === null || n === undefined) ? null : Number(n).toLocaleString();

const CSS = `
.cb-wrap{--navy:#0b2a4a;--gold:#a8842c;--paper:#fffdf6;--line:#ded7c6;--muted:#5b6672;
  --board:#7a2e2e;--board-2:#5e2222}
.cb-binder{display:flex;background:linear-gradient(180deg,var(--board),var(--board-2));
  border-radius:8px;padding:14px 14px 14px 0;box-shadow:0 16px 40px rgba(0,0,0,.3),inset 0 0 0 1px rgba(255,255,255,.08);
  height:calc(100vh - 210px);min-height:520px}
.cb-spine{width:46px;flex:0 0 46px;display:flex;flex-direction:column;align-items:center;
  justify-content:space-around;padding:34px 0}
.cb-ring{width:20px;height:20px;border-radius:50%;border:3px solid #cfd4d8;
  background:radial-gradient(circle at 35% 30%,#fff,#9aa3ab);box-shadow:0 1px 2px rgba(0,0,0,.4)}
.cb-tabs{width:250px;flex:0 0 250px;overflow-y:auto;padding:2px 0 12px}
.cb-div{position:relative;margin:0 0 3px 0;cursor:pointer;border:0;background:none;
  width:100%;text-align:left;padding:0;font:inherit}
.cb-div .cb-body{background:#f3f0e6;border-radius:3px 0 0 3px;padding:11px 14px 11px 13px;
  border-left:5px solid var(--gold);box-shadow:0 1px 2px rgba(0,0,0,.28);
  font-size:13.5px;color:#22303c;transition:transform .12s,background .12s}
.cb-div .cb-num{display:inline-block;min-width:20px;font-weight:700;color:var(--gold)}
.cb-div .cb-cnt{float:right;font-size:11.5px;color:#8a929b;font-weight:600;padding-top:2px}
.cb-div:hover .cb-body{background:#fffdf6;transform:translateX(3px)}
.cb-div.on .cb-body{background:var(--paper);transform:translateX(7px);border-left-color:var(--navy);
  box-shadow:0 2px 8px rgba(0,0,0,.35);font-weight:600}
.cb-div.empty .cb-body{background:#e6e3d9;color:#98a0a8}
.cb-div .cb-ear{position:absolute;right:-16px;width:16px;height:28px;border-radius:0 4px 4px 0;
  background:var(--gold);box-shadow:2px 1px 3px rgba(0,0,0,.3)}
.cb-div.on .cb-ear{background:var(--navy)}
.cb-div.empty .cb-ear{background:#b9b3a4}
.cb-view{flex:1;overflow-y:auto;background:#e8e2d4;border-radius:4px;padding:26px 20px 40px;
  box-shadow:inset 0 2px 10px rgba(0,0,0,.22);min-width:0}

.cb-cover{background:#0b0b0c;background-image:linear-gradient(157deg,#17171a 0%,#0b0b0c 46%,#000 100%);
  width:100%;max-width:880px;aspect-ratio:8.5/11;margin:0 auto 34px;padding:62px;border-radius:2px;
  border:1px solid #000;box-shadow:0 3px 6px rgba(0,0,0,.5),0 16px 40px rgba(0,0,0,.45),inset 0 1px 0 rgba(255,255,255,.07);
  text-align:center;position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center}
.cb-cover::before{content:"";position:absolute;inset:20px;border:1px solid rgba(255,255,255,.14);
  border-radius:2px;pointer-events:none}
.cb-cover .cb-kicker{color:rgba(255,255,255,.5);font-size:11.5px;letter-spacing:.34em;
  text-transform:uppercase;margin:0 0 30px;font-weight:600}
.cb-cover .cb-corp{color:#fff;font-size:33px;line-height:1.28;font-weight:600;letter-spacing:.045em;
  text-transform:uppercase;margin:0 auto;max-width:15ch;text-shadow:0 1px 0 rgba(0,0,0,.9),0 0 26px rgba(255,255,255,.14)}
.cb-cover .cb-rule{width:96px;height:1px;background:rgba(255,255,255,.42);margin:30px auto}
.cb-cover .cb-foot{color:rgba(255,255,255,.46);font-size:12px;letter-spacing:.14em;text-transform:uppercase;margin:0}
.cb-cover .cb-foot span+span::before{content:"\\00b7";margin:0 10px;color:rgba(255,255,255,.28)}

.cb-sheet{background:var(--paper);max-width:880px;margin:0 auto 34px;padding:52px 62px;
  border:1px solid var(--line);border-radius:2px;
  box-shadow:0 2px 5px rgba(0,0,0,.14),0 10px 26px rgba(0,0,0,.12);
  min-height:calc(880px * 11 / 8.5);padding-bottom:74px;position:relative;display:flex;flex-direction:column}
.cb-sheet h1{font-size:23px;margin:0 0 10px;color:var(--navy);border-bottom:2.5px solid var(--gold);
  padding-bottom:9px;letter-spacing:.01em}
.cb-sheet h2{font-size:17px;margin:26px 0 8px;color:var(--navy)}
.cb-sheet h3{font-size:13px;margin:20px 0 6px;color:var(--muted);font-weight:700;
  text-transform:uppercase;letter-spacing:.04em}
.cb-sheet p{margin:0 0 10px;font-size:14px;line-height:1.65}
.cb-sheet table{width:100%;border-collapse:collapse;margin:14px 0;font-size:13.5px}
.cb-sheet th,.cb-sheet td{border:1px solid var(--line);padding:7px 10px;text-align:left;vertical-align:top}
.cb-sheet th{background:#f1ece0;font-size:11.5px;text-transform:uppercase;letter-spacing:.04em;color:#4a5560}
.cb-sheet td.n{text-align:right;font-variant-numeric:tabular-nums}
.cb-sheet .cb-empty{color:#98a0a8;font-style:italic;font-size:14px}
.cb-pageno{position:absolute;left:0;right:0;bottom:30px;text-align:center;font-size:11.5px;
  color:#9a927f;letter-spacing:.16em}
.cb-pageno::before{content:"\\2014 "}.cb-pageno::after{content:" \\2014"}
.cb-dividerpage{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}
.cb-dp-num{font-size:11.5px;letter-spacing:.3em;text-transform:uppercase;color:var(--gold);font-weight:700;margin-bottom:14px}
.cb-dp-label{font-size:21px;color:var(--navy);letter-spacing:.02em;line-height:1.35}
.cb-dividerpage::after{content:"";display:block;width:70px;height:2px;background:var(--gold);margin-top:20px}

@media print{
  .cb-noprint{display:none!important}
  .cb-binder{display:block;box-shadow:none;background:none;padding:0;height:auto;min-height:0}
  .cb-spine,.cb-tabs{display:none!important}
  .cb-view{box-shadow:none;background:none;padding:0;overflow:visible}
  .cb-cover{aspect-ratio:auto;height:9.6in;max-width:none;margin:0;border:0;border-radius:0;
    box-shadow:none;page-break-after:always;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .cb-sheet{min-height:0;box-shadow:none;border:0;max-width:none;padding:0 0 24px;margin:0;
    page-break-after:always;display:block}
  .cb-sheet:last-child{page-break-after:auto}
  .cb-pageno{position:static;margin-top:18px}
}
@media(max-width:900px){
  .cb-binder{flex-direction:column;height:auto;padding:12px}
  .cb-spine{flex-direction:row;width:auto;flex:none;padding:6px 0}
  .cb-tabs{width:auto;flex:none;max-height:280px}
  .cb-div .cb-ear{display:none}.cb-div.on .cb-body{transform:none}
  .cb-cover{padding:34px 22px}.cb-cover .cb-corp{font-size:24px}
  .cb-sheet{min-height:0;padding:28px 24px 56px}
}
`;

const Row = ({ label, value }) => (
    <tr><th style={{ width: '38%' }}>{label}</th><td>{value || <span className="cb-empty">not on file</span>}</td></tr>
);

export default function CorporateBookPage() {
    const apiClient = useApiClient();
    const [binder, setBinder] = useState(undefined);
    const [error, setError] = useState('');
    const [openTab, setOpenTab] = useState(null);

    useEffect(() => {
        let alive = true;
        apiClient.get('/api/corporate/binder')
            .then(d => { if (alive) setBinder(d); })
            .catch(e => {
                if (!alive) return;
                if (String(e.message).includes('No corporation')) setBinder(null);
                else setError(e.message);
            });
        return () => { alive = false; };
    }, [apiClient]);

    // The book, composed as an ordered list of sheets. The cover is unnumbered, as a
    // board is; numbering starts on the index behind it.
    const pages = useMemo(() => {
        if (!binder) return [];
        const corp = binder.corporation;
        const out = [{ kind: 'cover' }];
        out.push({ kind: 'index' });

        for (const tab of binder.tabs) {
            out.push({ kind: 'divider', tab });
            if (tab.key === 'minutes') {
                const ms = tab.meetings || [];
                if (!ms.length) out.push({ kind: 'blank', tab, note: 'No meetings have been recorded.' });
                // One meeting to a sheet, the way minutes are filed.
                else ms.forEach(m => out.push({ kind: 'meeting', tab, meeting: m }));
            } else if (tab.key === 'shares') {
                out.push({ kind: 'shares', tab, corp });
            } else if (tab.key === 'resolutions') {
                out.push({ kind: 'resolutions', tab });
            } else {
                out.push({ kind: 'filed', tab });
            }
        }
        return out;
    }, [binder]);

    if (error) return <div className="p-6 text-red-600">{error}</div>;
    if (binder === undefined) return <div className="p-6 text-slate-500">Opening the book…</div>;
    if (binder === null) {
        return (
            <div className="p-6 max-w-2xl">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Corporate Record Book</h1>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <p className="text-amber-900 mb-4">
                        There is no corporation recorded yet, so there is no book to bind. Record the
                        corporation from its filed documents first.
                    </p>
                    <Link to="/dashboard/corporate-records" className="px-4 py-2 bg-amber-700 text-white rounded-md inline-block">
                        Go to the register
                    </Link>
                </div>
            </div>
        );
    }

    const corp = binder.corporation;
    const counts = {
        formation: (binder.tabs.find(t => t.key === 'formation')?.filed || []).length,
        bylaws: (binder.tabs.find(t => t.key === 'bylaws')?.filed || []).length,
        minutes: (binder.tabs.find(t => t.key === 'minutes')?.meetings || []).length,
        shares: (binder.tabs.find(t => t.key === 'shares')?.ledger || []).length,
        resolutions: (binder.tabs.find(t => t.key === 'resolutions')?.index || []).length,
        banking: (binder.tabs.find(t => t.key === 'banking')?.filed || []).length,
        state_filings: (binder.tabs.find(t => t.key === 'state_filings')?.filed || []).length,
        tax: (binder.tabs.find(t => t.key === 'tax')?.filed || []).length,
    };

    const scrollTo = (key) => {
        setOpenTab(key);
        const el = document.getElementById('cb-tab-' + key);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    let pageNo = 0;

    return (
        <div className="p-6 cb-wrap">
            <style>{CSS}</style>

            <div className="flex justify-between items-start mb-4 cb-noprint">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Corporate Record Book</h1>
                    <p className="text-slate-500 text-sm">
                        {corp.legal_name} — bound from the register. {' '}
                        <Link to="/dashboard/corporate-records" className="underline">Edit the register</Link>
                    </p>
                </div>
                <button onClick={() => window.print()} className="text-sm px-3 py-1.5 border rounded-md text-slate-600">
                    Print the book
                </button>
            </div>

            <div className="cb-binder">
                <div className="cb-spine cb-noprint">
                    <span className="cb-ring" /><span className="cb-ring" /><span className="cb-ring" />
                </div>

                <nav className="cb-tabs cb-noprint">
                    {binder.tabs.map(t => {
                        const n = counts[t.key] || 0;
                        return (
                            <button key={t.key} type="button"
                                className={`cb-div ${openTab === t.key ? 'on' : ''} ${n === 0 ? 'empty' : ''}`}
                                onClick={() => scrollTo(t.key)}>
                                <span className="cb-ear" style={{ top: 10 + (t.n - 1) * 4 }} />
                                <span className="cb-body">
                                    <span className="cb-num">{t.n}</span>{t.label}
                                    <span className="cb-cnt">{n || '—'}</span>
                                </span>
                            </button>
                        );
                    })}
                </nav>

                <div className="cb-view">
                    {pages.map((p, i) => {
                        if (p.kind === 'cover') {
                            return (
                                <div className="cb-cover" key={i}>
                                    <p className="cb-kicker">Corporate Record Book</p>
                                    <h2 className="cb-corp">{corp.legal_name}</h2>
                                    <div className="cb-rule" />
                                    <p className="cb-foot">
                                        <span>{corp.state === 'IL' ? 'Illinois' : corp.state}</span>
                                        {corp.state_file_number && <span>File {corp.state_file_number}</span>}
                                        {corp.incorporated_on && <span>{new Date(corp.incorporated_on + 'T00:00:00').getFullYear()}</span>}
                                    </p>
                                </div>
                            );
                        }
                        pageNo += 1;
                        const no = <div className="cb-pageno">{pageNo}</div>;

                        if (p.kind === 'index') {
                            return (
                                <div className="cb-sheet" key={i}>
                                    <h1>Index</h1>
                                    <table>
                                        <tbody>
                                            <Row label="Legal name" value={corp.legal_name} />
                                            <Row label="State of incorporation" value={corp.state === 'IL' ? 'Illinois' : corp.state} />
                                            <Row label="Secretary of State file number" value={corp.state_file_number} />
                                            <Row label="Employer identification number" value={corp.ein} />
                                            <Row label="Date of incorporation" value={fmt(corp.incorporated_on)} />
                                            <Row label="Fiscal year end" value={corp.fiscal_year_end} />
                                            <Row label="Registered agent" value={corp.registered_agent} />
                                            <Row label="Registered office" value={corp.registered_office} />
                                            <Row label="Shares authorised" value={num(corp.shares_authorized)} />
                                        </tbody>
                                    </table>

                                    <h3>Officers and directors</h3>
                                    {binder.officers.length === 0
                                        ? <p className="cb-empty">No officers recorded.</p>
                                        : (
                                            <table>
                                                <thead><tr><th>Office</th><th>Person</th><th>Serving as</th><th>Elected</th></tr></thead>
                                                <tbody>
                                                    {binder.officers.map(o => (
                                                        <tr key={o.officer_id}>
                                                            <td>{o.office}</td><td>{o.person}</td>
                                                            <td>{o.role_type}</td><td>{fmt(o.elected_on) || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}

                                    <h3>Contents</h3>
                                    <table>
                                        <tbody>
                                            {binder.tabs.map(t => (
                                                <tr key={t.key}>
                                                    <td style={{ width: '10%' }}>{t.n}</td>
                                                    <td>{t.label}</td>
                                                    <td className="n">{counts[t.key] || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {no}
                                </div>
                            );
                        }

                        if (p.kind === 'divider') {
                            return (
                                <div className="cb-sheet" key={i} id={'cb-tab-' + p.tab.key}>
                                    <div className="cb-dividerpage">
                                        <div className="cb-dp-num">Tab {p.tab.n}</div>
                                        <div className="cb-dp-label">{p.tab.label}</div>
                                    </div>
                                    {no}
                                </div>
                            );
                        }

                        if (p.kind === 'meeting') {
                            const m = p.meeting;
                            return (
                                <div className="cb-sheet" key={i}>
                                    <h1>{m.meeting_type === 'action_in_lieu' ? 'Action in lieu of meeting' : `${m.meeting_type.replace('_', ' ')} meeting`}</h1>
                                    <p><strong>{fmt(m.held_on)}</strong>{m.location ? ` · ${m.location}` : ''}</p>
                                    {m.status !== 'held' && (
                                        <p className="cb-empty">
                                            {m.status === 'scheduled'
                                                ? 'SCHEDULED — NOT YET HELD. This sheet is an agenda, not a minute.'
                                                : 'CANCELLED.'}
                                        </p>
                                    )}
                                    {m.fiscal_year && <p>Reporting on the fiscal year ended {m.fiscal_year}.</p>}
                                    {m.attendees && <><h3>Present</h3><p>{m.attendees}</p></>}
                                    {m.summary && <><h3>Minutes</h3><p style={{ whiteSpace: 'pre-wrap' }}>{m.summary}</p></>}
                                    {m.resolutions?.length > 0 && (
                                        <>
                                            <h3>Resolutions adopted</h3>
                                            <table>
                                                <thead><tr><th>Title</th><th>Type</th><th>Status</th></tr></thead>
                                                <tbody>
                                                    {m.resolutions.map(r => (
                                                        <tr key={r.resolution_id}>
                                                            <td>{r.title}</td><td>{r.resolution_type}</td><td>{r.status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                    {no}
                                </div>
                            );
                        }

                        if (p.kind === 'shares') {
                            const ledger = p.tab.ledger || [];
                            return (
                                <div className="cb-sheet" key={i}>
                                    <h1>Stock transfer ledger</h1>
                                    <p>
                                        The record of shareholders required by 805 ILCS 5/7.75. Cancelled
                                        certificates remain with their surrender date: this is the history of
                                        the shares, not a list of who holds them today.
                                    </p>
                                    <table>
                                        <tbody>
                                            <Row label="Shares authorised" value={num(corp.shares_authorized)} />
                                            <Row label="Shares issued and outstanding" value={num(corp.shares_issued)} />
                                        </tbody>
                                    </table>
                                    {ledger.length === 0
                                        ? <p className="cb-empty">No certificates have been recorded.</p>
                                        : (
                                            <table>
                                                <thead>
                                                    <tr><th>Cert.</th><th>Type</th><th>Holder</th><th>Shares</th>
                                                        <th>Consideration</th><th>Issued</th><th>Surrendered</th></tr>
                                                </thead>
                                                <tbody>
                                                    {ledger.map(s => (
                                                        <tr key={s.share_id}>
                                                            <td>{s.certificate_no}</td>
                                                            <td>{s.txn_type}</td>
                                                            <td>{s.holder}{s.from_holder ? ` (from ${s.from_holder})` : ''}</td>
                                                            <td className="n">{num(s.shares)}</td>
                                                            <td className="n">{s.consideration ? `$${Number(s.consideration).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>
                                                            <td>{fmt(s.issued_on)}</td>
                                                            <td>{fmt(s.surrendered_on) || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    {no}
                                </div>
                            );
                        }

                        if (p.kind === 'resolutions') {
                            const idx = p.tab.index || [];
                            return (
                                <div className="cb-sheet" key={i}>
                                    <h1>Resolution index</h1>
                                    <p>
                                        Resolutions adopted outside a meeting, each dated to the document it
                                        authorises. Business done at a meeting appears under Tab 3.
                                    </p>
                                    {idx.length === 0
                                        ? <p className="cb-empty">No standalone resolutions have been recorded.</p>
                                        : (
                                            <table>
                                                <thead><tr><th>Adopted</th><th>Title</th><th>Type</th><th>Counterparty</th><th>Status</th></tr></thead>
                                                <tbody>
                                                    {idx.map(r => (
                                                        <tr key={r.resolution_id}>
                                                            <td>{fmt(r.adopted_on) || '—'}</td>
                                                            <td>{r.title}{r.document_ref ? <><br /><em style={{ fontSize: 12, color: '#8a929b' }}>{r.document_ref}</em></> : null}</td>
                                                            <td>{r.resolution_type}</td>
                                                            <td>{r.counterparty || '—'}</td>
                                                            <td>{r.status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    {no}
                                </div>
                            );
                        }

                        // formation, bylaws, banking, state filings, tax
                        const filed = p.tab.filed || [];
                        const obs = p.tab.obligations || [];
                        return (
                            <div className="cb-sheet" key={i}>
                                <h1>{p.tab.label}</h1>
                                {obs.length > 0 && (
                                    <>
                                        <h3>Recurring filings</h3>
                                        <table>
                                            <thead><tr><th>Filing</th><th>Authority</th><th>Last filed</th><th>Next due</th></tr></thead>
                                            <tbody>
                                                {obs.map(o => (
                                                    <tr key={o.obligation_id}>
                                                        <td>{o.title}</td>
                                                        <td>{o.authority || '—'}</td>
                                                        <td>{fmt(o.last_completed_on) || '—'}</td>
                                                        <td>{fmt(o.next_due) || <em>to be set</em>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                                <h3>Filed documents</h3>
                                {filed.length === 0
                                    ? (
                                        <p className="cb-empty">
                                            Nothing filed under this tab yet.
                                            {p.tab.filed_elsewhere ? ` Documents for this tab are filed in the ${p.tab.filed_elsewhere}.` : ''}
                                        </p>
                                    )
                                    : (
                                        <table>
                                            <thead><tr><th>Document</th><th>Source</th><th>Date</th></tr></thead>
                                            <tbody>
                                                {filed.map(d => (
                                                    <tr key={d.document_id}>
                                                        <td>{d.title}</td>
                                                        <td>{d.original_name || '—'}</td>
                                                        <td>{fmt(d.document_date) || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                {no}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
